/**
 * Image utility functions
 * Handle product image URLs and placeholders
 */

/**
 * Placeholder image for products without images
 * Using a data URI for a simple gray placeholder
 */
const PLACEHOLDER_IMAGE =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%23e5e7eb"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="18" fill="%239ca3af"%3ENo Image Available%3C/text%3E%3C/svg%3E';

/**
 * Get Firebase Storage URL from relative path
 * Converts relative paths (e.g., "images/0001.png") to full Firebase Storage URLs
 *
 * @param imageUrl - Image path from database (relative or full URL)
 * @returns Full Firebase Storage URL or placeholder
 *
 * @example
 * ```typescript
 * getProductImageUrl('images/0001.png')
 * // Returns: https://firebasestorage.googleapis.com/v0/b/bucket/o/images%2F0001.png?alt=media
 *
 * getProductImageUrl(null)
 * // Returns: data:image/svg+xml... (placeholder)
 *
 * getProductImageUrl('https://example.com/image.jpg')
 * // Returns: https://example.com/image.jpg (unchanged)
 * ```
 */
export function getProductImageUrl(imageUrl?: string | null): string {
  // Return placeholder if no image URL provided
  if (!imageUrl || imageUrl.trim() === '') {
    return PLACEHOLDER_IMAGE;
  }

  // If already a full URL (starts with http:// or https://), return as-is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // Get Firebase Storage bucket from environment
  const storageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;

  if (!storageBucket) {
    console.error('Firebase Storage bucket not configured');
    return PLACEHOLDER_IMAGE;
  }

  // Remove leading slash if present
  const cleanPath = imageUrl.startsWith('/') ? imageUrl.slice(1) : imageUrl;

  // Encode the path for URL - encode each segment, then join with %2F
  // This ensures proper encoding without double-encoding slashes
  const encodedPath = cleanPath.split('/').map(encodeURIComponent).join('%2F');

  // Construct Firebase Storage URL
  const storageUrl = `https://firebasestorage.googleapis.com/v0/b/${storageBucket}/o/${encodedPath}?alt=media`;

  return storageUrl;
}

/**
 * Get placeholder image URL
 * Returns a gray SVG placeholder with "No Image Available" text
 *
 * @returns Data URI for placeholder SVG
 */
export function getPlaceholderImageUrl(): string {
  return PLACEHOLDER_IMAGE;
}

/**
 * Check if image URL is valid (not placeholder)
 *
 * @param imageUrl - Image URL to check
 * @returns True if valid image URL, false if placeholder or empty
 */
export function isValidImageUrl(imageUrl?: string | null): boolean {
  return Boolean(imageUrl && imageUrl !== PLACEHOLDER_IMAGE && imageUrl.trim() !== '');
}
